import { useCallback, useEffect, useReducer, useState } from 'react';

import store from 'js/lib/coursera.store';
import { getLanguageMatch, getUserLanguageCode } from 'js/lib/language';

import { useGetCourseLanguages } from 'bundles/common/queries/useGetCourseLanguages';
import { useSetUserLocaleMutation } from 'bundles/page-header/components/translation/useSetUserLocaleMutation';

function getNotificationStorageName(courseId: string, userLanguage: string, notificationType: string) {
  const LANGUAGE_NOTIFICATION_DISMISSED = 'LANGUAGE_NOTE_DISMISSED';
  return `${LANGUAGE_NOTIFICATION_DISMISSED}~${courseId}~${userLanguage}~${notificationType}`;
}

const useWasNotificationDismissed = (courseId: string, userLanguage: string, notificationType: string | undefined) => {
  const [isDismissed, dismiss] = useReducer(() => true, false);
  useEffect(() => {
    if (notificationType) {
      const wasDismissed = store.get(getNotificationStorageName(courseId, userLanguage, notificationType)) === true;
      if (wasDismissed) dismiss();
    }
  }, [dismiss, courseId, userLanguage, notificationType]);
  const onDismiss = useCallback<() => $TSFixMe>(() => {
    dismiss();
    if (notificationType) {
      store.set(getNotificationStorageName(courseId, userLanguage, notificationType), true);
    }
  }, [dismiss, courseId, userLanguage, notificationType]);

  return [isDismissed, onDismiss] as const;
};

const useGetNotificationType = (courseId: string, userLanguage: string) => {
  const { loading, autoTranslations, humanTranslations, originalLanguage, subtitles } = useGetCourseLanguages(courseId);

  if (loading || !originalLanguage) return null;
  if (getLanguageMatch(userLanguage, originalLanguage)) {
    // Original language, no need to show any dialog
    return null;
  }

  const hasSubtitles = subtitles.some((subtitleLanguage) => getLanguageMatch(subtitleLanguage, userLanguage));

  if (humanTranslations.some((translatedLanguage) => getLanguageMatch(translatedLanguage, userLanguage))) {
    return {
      type: 'HUMAN_TRANSLATED' as const,
      originalLanguage,
      hasSubtitles,
    };
  }

  if (autoTranslations.some((translatedLanguage) => getLanguageMatch(translatedLanguage, userLanguage))) {
    return {
      type: 'AUTO_TRANSLATED' as const,
      originalLanguage,
      hasSubtitles,
    };
  }
  return {
    type: 'NO_TRANSLATION' as const,
    originalLanguage,
    hasSubtitles,
  };
};

const useItemLanguageSwitcher = () => {
  const NON_ENGLISH_PREFERRED_LANGUAGE = 'NON_ENGLISH_PREFERRED_LANGUAGE';
  const setUserLocale = useSetUserLocaleMutation();

  const userLocale = getUserLanguageCode();
  // language code if user has used the switcher to go back to original language, false if the user is in their preferred language
  const [preferredLanguage, setPreferredLanguage] = useState<string | false>(false);

  useEffect(() => {
    const otherLanguage = store.get(NON_ENGLISH_PREFERRED_LANGUAGE);
    if (!otherLanguage || otherLanguage === userLocale) {
      setPreferredLanguage(false);
    } else {
      setPreferredLanguage(otherLanguage);
    }
  }, [userLocale, setPreferredLanguage]);

  return {
    preferredLanguage,
    switchLanguage: async (languageCode: string) => {
      await setUserLocale(languageCode);
      location.reload();
    },
    savePreferredLanguage: (languageCode: string | null) => {
      if (languageCode) {
        store.set(NON_ENGLISH_PREFERRED_LANGUAGE, languageCode);
      } else {
        store.remove(NON_ENGLISH_PREFERRED_LANGUAGE);
      }
    },
  };
};

const useGetUserLanguageTranslationType = (courseId: string) => {
  const userLanguage = getUserLanguageCode();

  const { loading, originalLanguage, translatedLanguages } = useGetCourseLanguages(courseId);

  if (loading || !originalLanguage || getLanguageMatch(userLanguage, originalLanguage)) return null;

  /**
   * First, find language exact match with the subtag (ex: pt-BR)
   */
  let userLanguageTranslationObj = (translatedLanguages || []).find((lang) => lang.languageCode === userLanguage);

  if (!userLanguageTranslationObj) {
    /**
     * If no exact match found then, look for a match without the subtag (ex: pt)
     */
    const userLanguageWoSubTag = userLanguage.replace(/-/, '_').split(/_/)[0];
    return (translatedLanguages || []).find((lang) => lang.languageCode === userLanguageWoSubTag)?.translationType;
  }

  return userLanguageTranslationObj?.translationType;
};

export {
  useWasNotificationDismissed,
  useGetNotificationType,
  useItemLanguageSwitcher,
  useGetUserLanguageTranslationType,
};
